/* eslint-disable @typescript-eslint/no-explicit-any */
import { Translation } from '@components/Flow';
import { ActionReducerMapBuilder, createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';
import { API_BASE_NO_API, Command, getAuthCookie, State, tabActions, tabTypes, WorkflowTrace } from '@store/index';
import { AxiosError, isAxiosError } from 'axios';
import { createRequestBody, eventHandler } from './execution/events';
import { Message } from '@common/Node/NodeSidebar/NodeInput/NodeMessageArray';
import { CallError, RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';

const DELIMITER = '[DELIMITER]';

interface WorkflowResponse {
  success: boolean;
  payload?: {
    error?: WorkflowNodeError;
  };
}

export type WorkflowError = {
  error: string;
};

export type WorkflowNodeError = {
  id: string | null;
  code: number;
  title: string;
  execution: NodeExecutionError;
  timestamp: number;
};

export type NodeExecutionError = {
  node_id: string;
  node: string;
  reason: string;
  parameters: any[];
  status: 'failed';
};

type RunWorkflow = {
  translation: Translation;
  workspace_id: string | undefined;
  workflow_id: string | undefined;
  parameters: RunWorkflowParameters;
  chatQuery?: any;
  abortController: AbortController;
  rtc?: RTC;
};

export type RunWorkflowParameters = {
  query: string | null;
  body: any | null;
  queryData: any | null;
  moduleParameters?: any | null;
  messageParameters?: MessageParameters | null;
};

export type MessageParameterType = string | { id: string; value: string }[] | 'SMS' | 'MMS';
export type MessageParameters = {
  type: 'SMS' | 'MMS';
  text: string;
  subject: string;
  media_urls: string[];
  entity_id: string | undefined;
};

export type WorkflowChat = {
  node_id: string;
  workflow_id: string;
  message_id: string;
  session_id: string;
  bot_id: string;
  query: string;
  // the template used to inject the query into the completion node
  template: string;
  messages: Message[];
};

export enum NodeTraceEvent {
  // workflow events
  WorkflowStart = 'workflow_start',
  WorkflowEnd = 'workflow_end',
  WorkflowError = 'workflow_error',
  // call events
  CallStart = 'call_start',
  CallEnd = 'call_end',
  // webhook events
  WebhookPending = 'webhook_pending',
  WebhookReceived = 'webhook_received',
  WebhookCancelled = 'webhook_cancelled',
  // node events
  NodeError = 'node_error',
  NodeStatus = 'node_status',
  NodeInput = 'node_input',
  NodeOutput = 'node_output',
  NodeDuration = 'node_duration',
  NodePlugin = 'node_plugin',
  NodeReset = 'node_reset',
  NodeJumpIncrement = 'node_jump_increment',
  NodeConditionalExecution = 'node_conditional_execution',
  // LLM completions
  NodeCompletionChat = 'chat',
  NodeCompletionToken = 'token',
  NodeCompletionTimer = 'timer',
  NodeCompletionComplete = 'complete',
  NodeCompletionToolSelected = 'function_selected',
  NodeCompletionToolInvoked = 'function_invoked',
  NodeCompletionToolRetry = 'function_retry',
  NodeCompletionTool = 'function',
  NodeCompletionToolError = 'function_error',
  NodeCompletionDocuments = 'documents',
  NodeCompletionMatches = 'matches',
  NodeCompletionError = 'error',
  NodeCompletionToolApproval = 'function_approval_required',
  // Messaging
  MessagingResponse = 'messaging_response',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNodeError = (payload: any): payload is { error: WorkflowNodeError } => {
  return payload && typeof payload.error !== 'undefined';
};

const getChatEligibleNodes = (commands: Command[]) => {
  const llmNodes = ['llm.completion', 'assistant.single', 'assistant.router'];

  return commands.filter((command) => {
    if (!llmNodes.includes(command.config.command_id)) return false;

    const dataConnections = command.connections.filter(
      (connection) => connection.target_anchor.variant === 'data' && connection.edge.source === command.node.id
    );

    if (dataConnections.length === 1) {
      const dataConnection = dataConnections[0];
      const targetNode = commands.find((command) => command.node.id === dataConnection.edge.target);
      const targetDataConnections =
        targetNode?.connections.filter(
          (connection) => connection.target_anchor.variant === 'data' && connection.edge.target === targetNode.node.id
        ) || [];

      return targetNode?.config.command_id === 'http.response' && targetDataConnections.length === 1;
    }
    return false;
  });
};

export const runWorkflow = createAsyncThunk<
  WorkflowResponse,
  RunWorkflow,
  { rejectValue: { error: WorkflowNodeError } }
>('tabs/run', async (payload: RunWorkflow, { dispatch, getState, rejectWithValue }) => {
  const { workspace_id, workflow_id, parameters, translation, abortController, rtc } = payload;

  const state = getState() as State;
  const theme = state.flow.theme;
  const isTelnyxUser = state.auth.isTelnyxUser;
  const internalRequests = state.auth.internalRequests;
  const executionLineColorPending = theme === 'dark' ? '#ffffff4a' : '#00000016';

  const edges = state.tabs.active.edges;
  const commands = state.tabs.active.commands;
  const modules = state.tabs.active.modules;
  const triggerCommand = commands.find((command) => command.config.trigger);

  if (!workspace_id || !workflow_id)
    return rejectWithValue({
      error: {
        id: null,
        code: 10005,
        title: 'Resource Not Found',
        timestamp: new Date().getTime(),
        execution: {
          node_id: triggerCommand?.node.id || '',
          node: triggerCommand?.config.identifier || '',
          reason: 'Workflow not found',
          status: 'failed',
          parameters: [],
        },
      },
    });

  try {
    const chatEligibleNodes = getChatEligibleNodes(commands);

    dispatch(tabActions.setWorkflowRunLoading({ running: true, parameters, chatNodes: chatEligibleNodes }));
    dispatch(tabActions.prepareEdgesForRun({ edges, stroke: executionLineColorPending }));
    dispatch(
      tabActions.setNodeRunStatus({ node_id: triggerCommand?.node?.id || '', workflow_id, status: 'processing' })
    );

    const query = parameters?.query || '';
    const requestBody = createRequestBody(parameters, translation);

    const getUrl = () => {
      // internal requests go directly to the internal service
      if (isTelnyxUser && internalRequests && !LOCAL) {
        const internalHost = window.location.origin.includes('dev')
          ? 'https://flow-server.query.dev.telnyx.io:433'
          : 'https://flow-server.query.prod.telnyx.io:433';

        return `${internalHost}/internal/execute/${workspace_id}/${workflow_id}?trace=flow${query}`;
      }

      return `${API_BASE_NO_API}/execute/${workspace_id}/${workflow_id}?trace=flow${query}`;
    };

    const getAuthHeaders = (): any => {
      if (LOCAL) {
        return {
          'telnyx-ratelimit-user-id': LOCAL_USER_ID,
          'telnyx-ratelimit-org-id': LOCAL_ORG_ID,
          'Content-Type': 'application/json',
        };
      }

      if (INTERNAL_CLIENT) {
        const userId = localStorage.getItem('internalUserId');
        const orgId = localStorage.getItem('internalOrgId');

        return {
          'telnyx-ratelimit-user-id': userId || '',
          'telnyx-ratelimit-org-id': orgId || '',
          'Content-Type': 'application/json',
        };
      }

      return { Authorization: `Bearer ${getAuthCookie()}`, 'Content-Type': 'application/json' };
    };

    const url = getUrl();
    const response = await fetch(url, {
      method: 'post',
      body: JSON.stringify(requestBody),
      headers: getAuthHeaders(),
      signal: abortController.signal,
    });

    if (!response.body)
      return rejectWithValue({
        error: {
          id: null,
          code: 10005,
          title: '',
          timestamp: new Date().getTime(),
          execution: {
            node_id: triggerCommand?.node.id || '',
            node: triggerCommand?.config.identifier || '',
            reason: 'Unable to initiate workflow run.',
            status: 'failed',
            parameters: [],
          },
        },
      });

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let streamDone = false;
    let buffer = '';

    while (!streamDone && reader && !state.tabs.active?.run?.aborted) {
      try {
        const { value: chunk, done } = await reader.read();
        if (done) {
          streamDone = true;
          dispatch(tabActions.stopWorkflowRun());
          break;
        }

        buffer += chunk;
        let delimiterIndex;

        while ((delimiterIndex = buffer.indexOf(DELIMITER)) !== -1) {
          const jsonString = buffer.slice(0, delimiterIndex);
          buffer = buffer.slice(delimiterIndex + DELIMITER.length);

          if (jsonString) {
            const { id, type, value } = JSON.parse(jsonString.trim());
            await eventHandler(
              id,
              type,
              value,
              dispatch,
              commands,
              modules,
              getState,
              workflow_id,
              theme,
              abortController,
              rtc
            );

            const endStreamEvents = [NodeTraceEvent.WorkflowEnd, NodeTraceEvent.WorkflowError];
            if (endStreamEvents.includes(type)) streamDone = true;
          }
        }
      } catch (error: any) {
        if (error?.message?.includes('abort')) {
          streamDone = true;
          return { success: true };
        }

        if (error?.type === 'call') {
          return handleCallError(error, state.tabs.active.run, triggerCommand, dispatch);
        }

        return handleServerDisconnection(triggerCommand, dispatch, rejectWithValue);
      }
    }

    return { success: true };
  } catch (error) {
    if (error === 'MANUAL_ABORT') return { success: true };

    if (isAxiosError(error)) return handleAxiosError(error, triggerCommand, dispatch, rejectWithValue);
    else handleUnexpectedError(triggerCommand, dispatch, rejectWithValue);
  }
});

/**
 * Used to handle workflow errors thrown from the backedn
 * @param error The axios error being thrown
 * @param trigger The trigger command of the workflow
 * @param dispatch A method to dispatch redux state updates
 * @param rejectWithValue A method for rejecting the redux thunk
 */

const handleAxiosError = (
  error: AxiosError,
  trigger: Command | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  rejectWithValue: any
) => {
  const message = error.response?.data as string;
  dispatch(tabActions.errorWorkflowRun(message));

  return rejectWithValue({
    error: {
      id: null,
      code: 10037,
      title: 'Service Unavailable',
      timestamp: new Date().getTime(),
      execution: {
        node_id: trigger?.node.id || '',
        node: trigger?.config.identifier || '',
        reason: message,
        status: 'failed',
        parameters: [],
      },
    },
  });
};

/**
 * Used to handle unexpected errors thrown during workflow execution from backend
 * @param trigger The trigger command of the workflow
 * @param dispatch A method to dispatch redux state updates
 * @param rejectWithValue A method for rejecting the redux thunk
 */

const handleUnexpectedError = (
  trigger: Command | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  rejectWithValue: any
) => {
  const message =
    'There was an unexpected error while initiating the workflow. Please contact support if this issue persists.';
  dispatch(tabActions.errorWorkflowRun(message));

  return rejectWithValue({
    error: {
      id: null,
      code: 10007,
      title: 'Unexpected error',
      timestamp: new Date().getTime(),
      execution: {
        node_id: trigger?.node.id || '',
        node: trigger?.config.identifier || '',
        reason: message,
        status: 'failed',
        parameters: [],
      },
    },
  });
};

/**
 * Thrown when the server disconnects while running the workflow
 * @param trigger The trigger command of the workflow
 * @param dispatch A method to dispatch redux state updates
 * @param rejectWithValue A method for rejecting the redux thunk
 */

const handleServerDisconnection = (
  trigger: Command | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  rejectWithValue: any
) => {
  const message =
    'There was an issue streaming the workflow from the receiving server. This is likely a temporary issue, please try again.';

  dispatch(tabActions.errorWorkflowRun(message));

  return rejectWithValue({
    error: {
      id: null,
      code: 40006,
      title: 'Workflow Streaming Error',
      timestamp: new Date().getTime(),
      execution: {
        node_id: trigger?.node.id || '',
        node: trigger?.config.identifier || '',
        reason: message,
        status: 'failed',
        parameters: [],
      },
    },
  });
};

/**
 * Used to handle any errors while initiating the inbound call
 * @param error The call error
 * @param trigger The trigger command of the workflow
 * @param state The entire workflow redux state
 * @param dispatch A method to dispatch redux state updates
 * @param rejectWithValue A method for rejecting the redux thunk
 */

export const handleCallError = (
  error: CallError,
  run: WorkflowTrace,
  trigger: Command | undefined,
  dispatch: ThunkDispatch<any, any, any>
) => {
  const errorMessage =
    error?.message || 'An unexpected error occured initiating the inbound call to start the workflow.';

  const trace = {
    id: run.id,
    type: NodeTraceEvent.WorkflowError,
    background: '#f35c5c',
    icon: 'workflow',
    name: 'Workflow Error',
    description: {
      node_id: null,
      node: null,
      reason: errorMessage,
    },
    showCollapse: false,
    collapsed: true,
  };

  dispatch(tabActions.addTrace({ node_id: null, trace, workflow_id: run.workflow_id }));
  dispatch(tabActions.errorWorkflowRun(errorMessage));

  dispatch(
    tabActions.addNodeError({
      id: trigger?.node?.id || '',
      name: '',
      error: {
        statusCode: error?.code,
        message: errorMessage,
      },
      fields: [],
      metadata: null,
      workflowId: run.workflow_id || '',
    })
  );
};

export const runWorkflowThunk = (builder: ActionReducerMapBuilder<tabTypes>) => {
  return [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    builder.addCase(runWorkflow.pending, (state: tabTypes) => {
      return;
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    builder.addCase(runWorkflow.fulfilled, (state: tabTypes) => {
      state.active.run.abortController = null;
      return;
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    builder.addCase(runWorkflow.rejected, (state: tabTypes) => {
      state.active.run.abortController = null;
      return;
    }),
  ];
};
